// @ts-strict-ignore
import {Directive, ElementRef, Injector, Input, OnChanges, OnInit, Optional, TemplateRef, ViewContainerRef, Renderer2} from "@angular/core";
import {Permissions} from "@models";
import {MatTooltip} from "@angular/material/tooltip";
import {PermissionTooltipDirective} from "./permission-tooltip.directive";

interface PermissionWrapper {
	active: boolean;
	hideElementInBackground: boolean;
	inactivateElement: boolean;
}

@Directive({
	selector: "[hasPermission]",
	providers: [MatTooltip],
	standalone: true,
})
export class HasPermissionDirective extends PermissionTooltipDirective implements OnInit, OnChanges {
	private static unavailableClass = "unavailable";
	private static permissionDebug = false;

	static isHost = "isHost";

	@Input({alias: "hasPermission", required: true})
	permission: keyof typeof Permissions | Permissions | "isHost";
	@Input() displayHiddenIfNoPermission = false;
	@Input() displayInactiveIfNoPermission = false;
	@Input() additionalPermissions: boolean[] | boolean;

	private embeddedViewCreated = false;

	constructor(
		injector: Injector,
		private renderer: Renderer2,
		@Optional() private host: ElementRef<HTMLElement>,
		@Optional() private templateRef: TemplateRef<unknown>,
		@Optional() private vcr: ViewContainerRef,
	) {
		super(injector);
	}

	ngOnInit(): void {
		typeof this.permission === "number"
			? (this.message = Permissions[this.permission])
			: (this.message = this.permission?.toString() ?? "No permission set");
		this.setupTooltip();

		this.updateView();
	}

	ngOnChanges(): void {
		this.updateView();
	}

	private updateView(): void {
		const perm = this.calcPermissions();
		if (!perm) return;

		// STRUCTURAL MODE: if we have a TemplateRef (e.g., on <ng-container>), render/clear content
		if (this.templateRef && this.vcr) {
			this.updateStructuralView(perm);
			return;
		}

		// ATTRIBUTE MODE: fall back to element style updates (no jQuery)
		if (!this.host) return; // nothing to do
		const el = this.host.nativeElement;

		// Special-case: cell-like elements marked with 'td' class
		if (el.classList.contains("td")) {
			if (perm.active) {
				this.renderer.removeClass(el, "inactive");
			} else {
				this.renderer.addClass(el, "inactive");
			}
			return;
		}

		if (perm.active) {
			if (HasPermissionDirective.permissionDebug) {
				this.renderer.setStyle(el, "border", "2px solid green");
			} else {
				this.renderer.removeClass(el, HasPermissionDirective.unavailableClass);
				this.renderer.removeStyle(el, "visibility");
				this.renderer.removeStyle(el, "display");
				this.renderer.removeStyle(el, "border");
			}
		} else {
			if (HasPermissionDirective.permissionDebug) {
				this.renderer.setStyle(el, "border", "2px solid red");
			} else if (perm.hideElementInBackground) {
				this.renderer.setStyle(el, "visibility", "hidden");
			} else if (perm.inactivateElement) {
				this.renderer.addClass(el, HasPermissionDirective.unavailableClass);
			} else {
				this.renderer.setStyle(el, "display", "none");
			}
		}
	}

	// For structural usage (*hasPermission on <ng-container> or any host with TemplateRef)
	private updateStructuralView(perm: PermissionWrapper): void {
		if (perm.active) {
			// Ensure view is visible/created
			if (!this.embeddedViewCreated) {
				this.vcr.createEmbeddedView(this.templateRef!);
				this.embeddedViewCreated = true;
			}
			// Clear any hidden/inactive styles previously applied
			this.walkHostNodes((node) => {
				if (node.nodeType === Node.ELEMENT_NODE) {
					const el = node as HTMLElement;
					this.renderer.removeClass(el, HasPermissionDirective.unavailableClass);
					this.renderer.removeStyle(el, "visibility");
					this.renderer.removeStyle(el, "border");
				}
			});
			if (HasPermissionDirective.permissionDebug) {
				this.walkHostNodes((node) => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						this.renderer.setStyle(node as HTMLElement, "border", "2px solid green");
					}
				});
			}
		} else {
			// No permission
			if (perm.hideElementInBackground || perm.inactivateElement) {
				// Render, but visually hide or mark unavailable
				if (!this.embeddedViewCreated) {
					this.vcr.createEmbeddedView(this.templateRef!);
					this.embeddedViewCreated = true;
				}
				this.walkHostNodes((node) => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						const el = node as HTMLElement;
						if (perm.hideElementInBackground) {
							this.renderer.setStyle(el, "visibility", "hidden");
						} else if (perm.inactivateElement) {
							this.renderer.addClass(el, HasPermissionDirective.unavailableClass);
						}
						if (HasPermissionDirective.permissionDebug) {
							this.renderer.setStyle(el, "border", "2px solid red");
						}
					}
				});
			} else {
				// Fully remove from DOM (default structural behavior)
				this.vcr.clear();
				this.embeddedViewCreated = false;
			}
		}
	}

	// Iterate all root nodes of the embedded view
	private walkHostNodes(fn: (n: Node) => void): void {
		if (!this.vcr) return;
		for (let i = 0; i < this.vcr.length; i++) {
			const viewRef = this.vcr.get(i) as any;
			// Angular keeps root nodes on _lView / rootNodes; safest is to use viewRef.rootNodes if present
			const nodes: Node[] = viewRef?.rootNodes ?? [];
			nodes.forEach(fn);
		}
	}

	private calcPermissions(): PermissionWrapper | null {
		if (!this.permission) return null;

		let value: string;
		if (typeof this.permission === "number") {
			value = Permissions[this.permission];
		} else if (this.permission === HasPermissionDirective.isHost) {
			value = this.permission;
		} else {
			value = this.permission.trim();
		}

		const notPermissionFlag = value[0] === "!";
		if (notPermissionFlag) value = value.slice(1).trim();

		const hasPermission = this.getHasPermission(value);
		const active = (hasPermission && !notPermissionFlag) || (!hasPermission && notPermissionFlag);

		return {
			active,
			hideElementInBackground: this.displayHiddenIfNoPermission,
			inactivateElement: this.displayInactiveIfNoPermission,
		};
	}

	private getHasPermission(value: string): boolean {
		if (value === HasPermissionDirective.isHost) {
			return this._userService.user.isHost;
		} else {
			if (Permissions[value] == null) {
				if (HasPermissionDirective.permissionDebug) {
					// Debug border only makes sense on attribute hosts; ignore if structural.
					if (this.host) {
						this.renderer.setStyle(this.host.nativeElement, "border", "2px solid orange");
					}
				}
				console.warn(value + " not in Permissions");
			}
			return this._userService.hasPermission(Permissions[value]) && this.checkAdditionalPermissions();
		}
	}

	private checkAdditionalPermissions(): boolean {
		if (Array.isArray(this.additionalPermissions)) {
			return this.additionalPermissions.reduce((total: boolean, current: boolean) => total && current, true);
		} else if (typeof this.additionalPermissions === "boolean") {
			return this.additionalPermissions;
		}
		return true;
	}
}
